<template>
  <div class="block-module">
    <!-- Instructions -->
    <p class="block-module__instructions">
      {{ mode === 'EDIT'
        ? $t('Add content to answer')
        : decodeHTML(moduleData.options.content) }}
    </p>

    <!-- Question -->
    <TEIError :validation-error="validationError" />
    <InteractiveModulePrompt
      v-if="mode === 'EDIT'"
      class="block-module__prompt"
      :canvas-name="canvasName"
      :module-data="clonedModuleData"
      :id="id"
      @update-module="validateAndUpdateTEI"
    />

    <div class="block-module__media-wrapper">
      <!-- Response media and replace button -->
      <template v-if="mode === 'VIEW' && (this.isUploading || uploadId || assetId)">
        <LoadingIndicator
          v-if="showLoadingIndicator"
          absolute
          contain-within
          class="block-module__loading"
          :text="loadingIndicatorText"
        />
        <MediaViewer
          v-else-if="upload"
          :thumbnail-url="uploadThumbnailUrl"
          :media-url="mediaUrl"
          :asset-group="assetGroup"
          :scale="scale"
          :video="video"
          :is-displayed="isDisplayed"
          :theme-name="themeName"
          class="block-module__media"
        />
        <MediaViewer
          v-else
          :thumbnail-url="assetThumbnailUrl"
          :media-url="mediaUrl"
          :asset-group="assetGroup"
          :player-link="usePlayer ? playerLink : ''"
          :scale="scale"
          :video="video"
          :type="asset.type.name"
          :mode="mode"
          :is-displayed="isDisplayed"
          :theme-name="themeName"
          class="block-module__media"
        />
        <div
          v-if="userCanEditAttempt"
          class="block-module__replace-button"
        >
          <AddModule
            :module-id="id"
            :allow-drag="false"
            :canvas-name="canvasName"
            theme="replace"
            :scale="scale"
            :on-my-content-click="() => openMyContentModal()"
            :on-quicklist-click="() => openQuicklistModal()"
            :on-search-click="() => openSearchAssetsModal()"
            :upload-allow-multiple="false"
            :upload-callback="onConfirm"
          >
            <template #trigger-button="{ toggleMenu }">
              <button
                class="comet-button comet-button--light comet-button--icon-with-text"
                @click="toggleMenu"
              >
                <span class="comet-button__inner">
                  <NebulaIcon
                    symbol-id="refresh"
                    class-override="comet-button__icon comet-button__icon--left"
                  />
                  <span class="comet-button__text">
                    {{ $t('Replace') }}
                  </span>
                </span>
              </button>
            </template>
          </AddModule>
        </div>
      </template>

      <!-- Response -->
      <div
        class="block-module__response-stage"
        v-else-if="showResponse"
      >
        <!-- Non-interactive response preview for teachers -->
        <div
          v-if="mode === 'EDIT' || !userCanEditAttempt"
          class="comet-button comet-button--icon-with-text
            comet-button--disabled block-module__add-content"
        >
          <span class="comet-button__inner">
            <NebulaIcon
              class-override="comet-button__icon comet-button__icon--left"
              symbol-id="add"
            />
            <span class="comet-button__text">
              {{ $t('Add Content') }}
            </span>
          </span>
        </div>
        <!-- Interactive response for students -->
        <AddModule
          v-else
          :module-id="id"
          :allow-drag="false"
          :canvas-name="canvasName"
          theme="replace"
          :scale="scale"
          :on-my-content-click="() => openMyContentModal()"
          :on-quicklist-click="() => openQuicklistModal()"
          :on-search-click="() => openSearchAssetsModal()"
          :upload-allow-multiple="false"
          :upload-callback="onConfirm"
        >
          <template #trigger-button="{ toggleMenu }">
            <button
              class="comet-button comet-button--primary
                comet-button--icon-with-text block-module__add-content"
              @click="toggleMenu"
            >
              <span class="comet-button__inner">
                <NebulaIcon
                  class-override="comet-button__icon comet-button__icon--left"
                  symbol-id="add"
                />
                <span class="comet-button__text">
                  {{ $t('Add Content') }}
                </span>
              </span>
            </button>
          </template>
        </AddModule>
      </div>
    </div>
  </div>
</template>

<script>
import { NebulaIcon } from '@discoveryedu/nebula-components';
import { get } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import assetMixin from '@/mixins/asset';
import moduleData from '@/mixins/moduleData';
import teiValidation from '@/mixins/teiValidation';
import AddModule from '@/components/AddModule.vue';
import * as types from '@/lib/constants/store';
import { decodeHTML } from '@/lib/utils';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import MediaViewer from '@/components/MediaViewer.vue';
import { getGroupFromFileType, PROCESSING_ASSET_STATUSES } from '@/lib/constants';
import { MP3_GENERAL_AUDIO_FORMAT_ID } from '@/lib/constants/mediaFileFormatIds';
import InteractiveModulePrompt from './InteractiveModulePrompt.vue';
import TEIError from './TEIError.vue';
import {
  useAssessmentStore,
  useAssetStore,
  useEditorStore,
  useModalStore,
  useUploadStore,
  useUserStore,
} from '@/stores';

export default {
  name: 'BlockModule',
  components: {
    AddModule,
    NebulaIcon,
    InteractiveModulePrompt,
    LoadingIndicator,
    MediaViewer,
    TEIError,
  },
  mixins: [
    assetMixin,
    moduleData,
    teiValidation,
  ],
  props: {
    active: Boolean,
    canvasName: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      required: true,
    },
    mode: {
      type: String,
      required: true,
    },
    moduleData: {
      type: Object,
      required: true,
    },
    scale: {
      type: Number,
      default: 1,
    },
    isDisplayed: {
      type: Boolean,
      default: true,
    },
    page: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapState(useEditorStore, [
      'region',
      'currentPage',
      'editorIsFullscreen',
    ]),
    ...mapState(useUserStore, [
      'user',
      'userIsStudent',
    ]),
    ...mapState(useAssessmentStore, [
      'attempt',
      'attemptItemByTeiId',
      'userCanEditAttempt',
    ]),
    ...mapState(useAssetStore, [
      'assets',
    ]),
    ...mapState(useModalStore, [
      'modals',
    ]),
    ...mapState(useUploadStore, [
      'uploads',
    ]),
    asset() {
      if (!this.assetId) return null;
      return this.assets[this.assetId];
    },
    assetId() {
      return get(this.attemptItemByTeiId(this.teiId), 'try.response.moduleData.assetId');
    },
    assetGroup() {
      if (this.asset) return this.asset.group;

      return getGroupFromFileType(this.upload.content_type);
    },
    isUploading() {
      return PROCESSING_ASSET_STATUSES.includes(this.asset?.status?.id);
    },
    loadingIndicatorText() {
      if (PROCESSING_ASSET_STATUSES.includes(this.asset?.status?.id)) {
        return this.$t('Processing. Please wait.');
      }

      if (this.uploadId && !this.upload) {
        return this.$t('Uploading. Please wait.');
      }

      return null;
    },
    mediaUrl() {
      // Only audio has a media url
      if (this.assetGroup === 'audio') {
        if (this.upload) return this.upload.url;
        if (this.asset.is_user_created) return get(this, 'asset.media_files[0].url');

        const audioFile = this.asset.media_files
          .find((mediaFile) => mediaFile.format_id === MP3_GENERAL_AUDIO_FORMAT_ID);

        return audioFile && audioFile.url;
      }

      return null;
    },
    uploadSources() {
      if (this.assetGroup !== 'video') return [];

      return [{
        type: this.upload.content_type,
        url: this.upload.url,
      }];
    },
    showLoadingIndicator() {
      return (
        this.isUploading // upload processing
        || (this.uploadId && !this.upload) // upload not loaded yet
        || (this.assetId && !this.asset) // asset not loaded yet
      );
    },
    teiId() {
      if (!this.moduleData || !this.moduleData.references) return null;

      const teiReference = get(this, 'moduleData.references', [])
        .find((ref) => ref.reference_type === 'asset');
      return teiReference && teiReference.reference_id;
    },
    uploadThumbnailUrl() {
      // As of now, videos don't have thumbnails
      if (['video', 'audio'].includes(this.assetGroup)) {
        return null;
      }

      return {
        thumbnailLoaderId: this.uploadId,
        imageUrls: [
          { url: this.upload.url, isThumbnail: false },
        ],
      };
    },
    upload() {
      if (!this.uploadId) return null;
      return this.uploads[this.uploadId];
    },
    uploadId() {
      return get(this.attemptItemByTeiId(this.teiId), 'try.response.moduleData.uploadId');
    },
    video() {
      if ((!this.asset && !this.upload)
        || this.assetGroup !== 'video'
        || (this.asset && !this.assetVideoMediaFiles)) return undefined;

      if (this.asset) {
        // Videos uploaded to My Content don't need much formatting
        return {
          ...this.asset,
          media_files: this.assetVideoMediaFiles,
          closed_captions: this.assetClosedCaptions,
          duration_in_seconds: this.asset.duration,
        };
      }

      // Videos uploaded using the old Studio upload api need their data rearranged to
      // look like an asset
      return {
        uploadId: this.upload?.id,
        closed_captions: [],
        duration_in_seconds: null,
        media_files: [{
          format: {},
          type: this.upload.content_type,
          url: this.upload.url,
        }],
        title: this.upload.filename,
        type: {
          name: 'Full Video',
        },
      };
    },
    showResponse() {
      /*
        Hide the response section anytime the editor is fullscreen and
        no attempt data is loaded.
      */
      return !(this.editorIsFullscreen && !this.attempt);
    },
    themeName() {
      return (this.page || this.currentPage)?.options?.theme?.name;
    },
  },
  watch: {
    assetId(id) {
      if (id) this[types.CACHE_ASSET](id);
    },
    uploadId(id) {
      if (id) this[types.CACHE_UPLOAD](id);
    },
  },
  methods: {
    ...mapActions(useAssetStore, [
      types.CACHE_ASSET,
    ]),
    ...mapActions(useUploadStore, [
      types.CACHE_UPLOAD,
    ]),
    ...mapActions(useModalStore, [
      types.OPEN_MODAL,
      types.SET_MY_CONTENT_MODAL_OPTIONS,
    ]),
    ...mapActions(useAssessmentStore, [
      types.UPDATE_RESPONSE,
    ]),
    decodeHTML,
    onConfirm(assetSelection) {
      // This method is only meant to be used as a callback for the asset-selection modals

      const assetId = assetSelection[0].id;
      const url = `${window.location.href}&user_id=${this.user.id}&source=grade_assignment`;
      const linkText = this.$t('View response in Studio');
      const html = `<a href="${url}" target="_blank" data-is-studio-quiz-link="true">${linkText}</a>`;

      this[types.UPDATE_RESPONSE]({
        teiId: this.teiId,
        payload: {
          moduleData: { assetId },
          answerFieldData: [html],
        },
      });
    },
    openMyContentModal() {
      this[types.SET_MY_CONTENT_MODAL_OPTIONS]({
        config: {
          hideDistrictTab: this.region === 'UK', // see DEX-2646
          isActive: true,
          isStudent: this.userIsStudent,
          selectMultiple: false,
        },
        onChooseAssets: this.onConfirm,
      });
    },
    openSearchAssetsModal() {
      this[types.OPEN_MODAL]({
        type: 'SearchAssets',
        singleSelect: true,
        onConfirm: this.onConfirm,
      });
    },
    openQuicklistModal() {
      this[types.OPEN_MODAL]({
        type: 'FromQuicklist',
        singleSelect: true,
        onConfirm: this.onConfirm,
      });
    },
  },
};
</script>

<style lang="stylus">
.block-module {
  background-color: $nebula-color-white;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: $nebula-space-2x;

  &__instructions {
    color: $comet-color-neutral-50;
    font-size: $nebula-font-size-caption;
    font-weight: bold;
    margin: 0 0 $nebula-space-2x 0;
  }

  &__media-wrapper {
    display: flex;
    flex: 1 1 100%;
    min-height: 58px;
    position: relative;

    .block-module__media {
      flex: 1 1 100%;
      height: auto;
    }

    .block-module__add-content {
      padding: 10px;
    }
  }

  &__replace-button {
    font-feature-settings: 'liga' 1, 'ss01' 0; // UI buttons do not use schoolbook styles
    position: absolute;
    right: $nebula-space-1x;
    top: $nebula-space-1x;
    z-index: 101; // above audio wave

    .comet-button {
      margin: 0;
    }
  }

  &__response-stage {
    align-items: center;
    background-color: $comet-background-color-light;
    border-radius: 6px; // match module styles
    display: flex;
    height: 100%;
    justify-content: center;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;

    .comet-button {
      font-feature-settings: 'liga' 1, 'ss01' 0; // UI buttons do not use schoolbook styles
      margin: 0;
    }
  }

  &__loading {
    border-radius: $nebula-border-radius-badge-default;
    z-index: inherit;
  }

  html[dir="rtl"] & {
    .block-module__add-content {
      margin: 0;
    }
  }
}
</style>
